/* Multi-Kit Selector — Variant LP only.
 * Owns: selection state, cart re-add, analytics dispatch.
 *
 * Cart re-add flow (on CTA submit):
 *   1. Read /cart.js to find any existing line items for the kit variant.
 *   2. POST /cart/update.js with quantity=0 for each matching line (removes).
 *   3. POST /cart/add.js with new quantity.
 *   4. window.location = '/checkout' (matches existing lp-cta redirect behavior).
 *
 * Other cart line items (non-kit) are left untouched. Future seed packs,
 * accessories, etc. survive the re-add.
 *
 * Analytics events (window.dataLayer.push if available, console.log fallback
 * for dev visibility):
 *   - setup_selector_viewed     (fires once on first viewport entry)
 *   - setup_option_selected     (fires on each selection change)
 *   - add_to_cart_clicked       (fires on CTA submit, before fetch)
 *   - cart_created_or_updated   (fires after /cart/add.js succeeds)
 */

(function () {
  'use strict';

  var EXPERIMENT_VARIANT = 'variant';
  var SELECTOR_FORM = '[data-multi-kit-selector]';
  var CTA_SELECTOR = '[data-selector-cta]';

  function dispatch(event, params) {
    var payload = Object.assign({ event: event, experiment_variant: EXPERIMENT_VARIANT }, params || {});
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push(payload);
    } else if (window.console && console.debug) {
      console.debug('[multi-kit-selector]', payload);
    }
  }

  function getSelectedRadio(form) {
    return form.querySelector('input[name="multi_kit_tier"]:checked');
  }

  function updateVisualState(form) {
    var rows = form.querySelectorAll('.lp-selector-row');
    rows.forEach(function (row) {
      var input = row.querySelector('input[name="multi_kit_tier"]');
      row.setAttribute('data-selected', input.checked ? 'true' : 'false');
    });
  }

  function updateCtaLabel(form) {
    var selected = getSelectedRadio(form);
    if (!selected) return;
    var cta = form.querySelector(CTA_SELECTOR);
    if (!cta) return;
    var name = selected.getAttribute('data-tier-name');
    var price = selected.getAttribute('data-tier-price');
    cta.textContent = 'Get the ' + name + ' — $' + price;
  }

  function updateStickyState(form) {
    var selected = getSelectedRadio(form);
    if (!selected) return;
    var sticky = document.querySelector('[data-sticky-cta]');
    if (!sticky) return;
    var nameEl = sticky.querySelector('[data-sticky-name]');
    var priceEl = sticky.querySelector('[data-sticky-price]');
    if (nameEl) nameEl.textContent = selected.getAttribute('data-tier-name');
    if (priceEl) priceEl.textContent = '$' + selected.getAttribute('data-tier-price');
  }

  function updateFinalCtaState(form) {
    var selected = getSelectedRadio(form);
    if (!selected) return;
    // The final CTA section renders <a href="#shop-buy" class="lp-cta lp-cta--lg">
    // in the variant template. We retarget it to a direct add-to-cart and
    // sync its label with the current selection.
    var anchors = document.querySelectorAll('.lp-finalcta a.lp-cta');
    var name = selected.getAttribute('data-tier-name');
    var price = selected.getAttribute('data-tier-price');
    anchors.forEach(function (a) {
      a.textContent = 'Get the ' + name + ' — $' + price;
      a.setAttribute('data-final-cta-smart', 'true');
    });
  }

  function handleSelectionChange(form) {
    updateVisualState(form);
    updateCtaLabel(form);
    updateStickyState(form);
    updateFinalCtaState(form);

    var selected = getSelectedRadio(form);
    if (!selected) return;

    dispatch('setup_option_selected', {
      setup_name: selected.getAttribute('data-tier-name'),
      kit_quantity: parseInt(selected.getAttribute('data-tier-quantity'), 10),
      setup_price: parseInt(selected.getAttribute('data-tier-price'), 10)
    });
  }

  function fetchJSON(url, options) {
    return fetch(url, Object.assign({
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
    }, options || {})).then(function (res) {
      if (!res.ok) {
        return res.text().then(function (text) {
          throw new Error('Cart API ' + res.status + ': ' + text);
        });
      }
      return res.json();
    });
  }

  function removeExistingKitLines(variantId) {
    return fetchJSON('/cart.js').then(function (cart) {
      var matching = (cart.items || []).filter(function (item) {
        return String(item.variant_id) === String(variantId);
      });
      if (matching.length === 0) return Promise.resolve(null);

      var updates = {};
      updates[variantId] = 0;
      return fetchJSON('/cart/update.js', {
        method: 'POST',
        body: JSON.stringify({ updates: updates })
      });
    });
  }

  function addNewQuantity(variantId, quantity) {
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: parseInt(quantity, 10) }]
      })
    });
  }

  function handleSubmit(event, form) {
    event.preventDefault();
    if (form.getAttribute('data-submitting') === 'true') return;
    var selected = getSelectedRadio(form);
    if (!selected) return;

    var variantId = form.getAttribute('data-variant-id');
    var quantity = parseInt(selected.getAttribute('data-tier-quantity'), 10);
    var setupName = selected.getAttribute('data-tier-name');
    var setupPrice = parseInt(selected.getAttribute('data-tier-price'), 10);

    if (!variantId) {
      console.error('[multi-kit-selector] Missing data-variant-id on form. Aborting.');
      return;
    }

    dispatch('add_to_cart_clicked', {
      selected_setup_name: setupName,
      kit_quantity: quantity,
      setup_price: setupPrice
    });

    form.setAttribute('data-submitting', 'true');

    removeExistingKitLines(variantId)
      .then(function () { return addNewQuantity(variantId, quantity); })
      .then(function (cart) {
        dispatch('cart_created_or_updated', {
          selected_setup_name: setupName,
          kit_quantity: quantity,
          expected_total_price: setupPrice * 100,
          actual_cart_total: cart && cart.items_subtotal_price ? cart.items_subtotal_price : null
        });
        window.location = '/checkout';
      })
      .catch(function (err) {
        console.error('[multi-kit-selector] Cart update failed:', err);
        form.setAttribute('data-submitting', 'false');
        alert('Something went wrong adding to cart. Please try again.');
      });
  }

  function setupViewportObserver(form) {
    if (!('IntersectionObserver' in window)) {
      dispatch('setup_selector_viewed', { page_url: window.location.href });
      return;
    }
    var fired = false;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !fired) {
          fired = true;
          dispatch('setup_selector_viewed', { page_url: window.location.href });
          observer.disconnect();
        }
      });
    }, { threshold: 0.25 });
    observer.observe(form);
  }

  function setupStickyCtaVisibility(form) {
    var sticky = document.querySelector('[data-sticky-cta]');
    if (!sticky) return;
    if (!('IntersectionObserver' in window)) {
      // No IO support: leave sticky permanently visible on mobile.
      sticky.setAttribute('data-sticky-visible', 'true');
      sticky.setAttribute('aria-hidden', 'false');
      return;
    }
    // Sticky becomes visible when the main selector form scrolls OFF screen.
    // When the selector is in view, the sticky is redundant so we hide it.
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var visible = !entry.isIntersecting;
        sticky.setAttribute('data-sticky-visible', visible ? 'true' : 'false');
        sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
      });
    }, { threshold: 0 });
    observer.observe(form);
  }

  function setupStickyCtaTrigger(form) {
    var trigger = document.querySelector('[data-sticky-trigger]');
    if (!trigger) return;
    trigger.addEventListener('click', function () {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        // Fallback for browsers without form.requestSubmit().
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    });
  }

  function setupFinalCtaSmartClick(form) {
    // Convert the final CTA anchor (.lp-finalcta a.lp-cta) into a direct
    // add-to-cart that uses the current selector state. The static anchor
    // markup still works as a scroll-to-selector fallback for no-JS.
    var anchors = document.querySelectorAll('.lp-finalcta a.lp-cta');
    anchors.forEach(function (a) {
      a.addEventListener('click', function (event) {
        // Only intercept if there's a current selection; otherwise let the
        // anchor's default scroll-to-#shop-buy behavior run.
        var selected = getSelectedRadio(form);
        if (!selected) return;
        event.preventDefault();
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      });
    });
  }

  function init() {
    var form = document.querySelector(SELECTOR_FORM);
    if (!form) return;

    updateVisualState(form);
    updateCtaLabel(form);
    updateStickyState(form);
    updateFinalCtaState(form);

    form.addEventListener('change', function (event) {
      if (event.target && event.target.name === 'multi_kit_tier') {
        handleSelectionChange(form);
      }
    });

    form.addEventListener('submit', function (event) {
      handleSubmit(event, form);
    });

    setupViewportObserver(form);
    setupStickyCtaVisibility(form);
    setupStickyCtaTrigger(form);
    setupFinalCtaSmartClick(form);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
